import { cn } from "@/lib/utils";
import {
    FloatingArrow,
    FloatingPortal,
    Placement,
    arrow,
    autoUpdate,
    flip,
    offset,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useMergeRefs,
    useRole
} from "@floating-ui/react";
import Color from "color";
import {
    HTMLProps,
    ReactNode,
    cloneElement,
    createContext,
    forwardRef,
    isValidElement,
    useContext,
    useMemo,
    useRef,
    useState
} from "react";

interface TooltipOptions {
    disabled?: boolean;
    initialOpen?: boolean;
    placement?: Placement;
    trigger?: "mouseover" | "mousedown";
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

const ARROW_WIDTH = 10;
const ARROW_HEIGHT = 6;
const ARROW_TIP_RADIUS = 2;
const GAP = 4;

export function useTooltip({
    disabled = false,
    initialOpen = false,
    placement = "top",
    trigger = "mouseover",
    open: controlledOpen,
    onOpenChange: setControlledOpen
}: TooltipOptions = {}) {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);

    const arrowRef = useRef(null);

    const open = controlledOpen ?? uncontrolledOpen;
    const setOpen = setControlledOpen ?? setUncontrolledOpen;

    const data = useFloating({
        placement,
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(ARROW_HEIGHT + GAP),
            flip({
                crossAxis: placement.includes("-"),
                fallbackAxisSideDirection: "start",
                padding: GAP
            }),
            shift({ padding: GAP }),
            arrow({
                element: arrowRef
            })
        ]
    });

    const context = data.context;

    const hover = useHover(context, {
        move: false,
        enabled: controlledOpen == null && trigger === "mouseover" && !disabled
    });
    const focus = useFocus(context, {
        enabled: controlledOpen == null && !disabled
    });
    const click = useClick(context, {
        enabled: controlledOpen == null && trigger === "mousedown" && !disabled,
        event: "mousedown"
    });
    const dismiss = useDismiss(context, { ancestorScroll: true });
    const role = useRole(context, { role: "tooltip" });

    const interactions = useInteractions([hover, focus, click, dismiss, role]);

    return useMemo(
        () => ({
            open,
            setOpen,
            ...interactions,
            ...data,
            arrowRef
        }),
        [open, setOpen, interactions, data]
    );
}

type ContextType = ReturnType<typeof useTooltip> | null;

const TooltipContext = createContext<ContextType>(null);

export const useTooltipContext = () => {
    const context = useContext(TooltipContext);

    if (context == null) {
        throw new Error("Tooltip components must be wrapped in <Tooltip />");
    }

    return context;
};

export function Tooltip({
    children,
    ...options
}: { children: ReactNode } & TooltipOptions) {
    // This can accept any props as options, e.g. `placement`,
    // or other positioning options.
    const tooltip = useTooltip(options);

    return (
        <TooltipContext.Provider value={tooltip}>
            {children}
        </TooltipContext.Provider>
    );
}

const TooltipTrigger = forwardRef<
    HTMLElement,
    HTMLProps<HTMLElement> & { asChild?: boolean }
>(function TooltipTrigger({ children, asChild = true, ...props }, propRef) {
    const context = useTooltipContext();
    const childrenRef = (children as any).ref;
    const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

    // `asChild` allows the user to pass any element as the anchor
    if (asChild && isValidElement(children)) {
        return cloneElement(
            children,
            context.getReferenceProps({
                ref,
                ...props,
                ...(children.props as any),
                "data-state": context.open ? "open" : "closed"
            }) as any
        );
    }

    return (
        <div
            ref={ref}
            // The user can style the trigger based on the state
            data-state={context.open ? "open" : "closed"}
            {...context.getReferenceProps(props)}
        >
            {children}
        </div>
    );
});

const TooltipContent = forwardRef<
    HTMLDivElement,
    HTMLProps<HTMLDivElement> & {
        className?: string;
        backgroundColor?: string;
        color?: string;
    }
>(function TooltipContent(
    { children, backgroundColor = "#000", color = "#fff", className, ...props },
    propRef
) {
    const { context: floatingContext, ...context } = useTooltipContext();
    const ref = useMergeRefs([context.refs.setFloating, propRef]);

    if (!context.open) return null;

    const bgColor = Color(backgroundColor).alpha(0.75).string();

    return (
        <FloatingPortal id="tooltip-portal">
            <div
                ref={ref}
                style={{
                    ...context.floatingStyles,
                    backgroundColor: bgColor,
                    color,
                    zIndex: 100000000
                }}
                className={cn(
                    "rounded-sm px-[var(--gap-padding-x-large,16px)] py-2.5",
                    className
                )}
                {...context.getFloatingProps(props)}
            >
                {children}
                <FloatingArrow
                    ref={context.arrowRef}
                    context={floatingContext}
                    width={ARROW_WIDTH}
                    height={ARROW_HEIGHT}
                    tipRadius={ARROW_TIP_RADIUS}
                    fill={bgColor}
                />
            </div>
        </FloatingPortal>
    );
});

Tooltip.Trigger = TooltipTrigger;
Tooltip.Content = TooltipContent;
